import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  ViewChild,
  ElementRef,
  computed,
  signal,
} from '@angular/core';
import {
  MatDialog,
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogClose,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  UntypedFormControl,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { AddressValidationService } from 'src/app/services/address-validation.service';
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { moveItemInArray, CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { firstValueFrom, Subscription } from 'rxjs';
import { UserRideComponent } from './components/user-ride/user-ride.component';
import { CopyUpdateComponent } from '../../dialogs/copy-update/copy-update.component';
import { DeleteRideComponent } from './dialogs/delete-ride/delete-ride.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatAutocomplete,
} from '@angular/material/autocomplete';
import {
  MatChipInputEvent,
  MatChipGrid,
  MatChipRow,
  MatChipRemove,
  MatChipInput,
  MatChipListbox,
  MatChipOption,
  MatChipSelectionChange,
} from '@angular/material/chips';
import moment from 'moment';
import {
  Product,
  Ride,
  Vehicle,
  DaypartDay,
  User,
  Particularity,
  Rate,
  DaypartException,
  GenericWarningDialogData,
  DataStoreExport,
  UserInRide,
  RideLocation,
  NotificationsParticipants,
} from 'src/app/interfaces';
import { getDateString, valueToDate } from 'src/app/globals';
import { getObjectDifferences } from 'ts-object-difference';
import { NotifyUsersRideDialog } from './dialogs/notify-users-ride/notify-users-ride.component';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import {
  buildLocationsOrder,
  calculateAutomaticRideOccupancy,
  checkUserDoubleBooking,
  createRetourRide,
  getExceptionsForDay,
  getFullLocationString,
  getRideTitle,
  getTimeSlots,
  getUserById,
  getWeekString,
  onMapsClick,
} from '../../helpers/ride-helper';
import { FilterOption, PlanType, Role } from 'src/app/enums';
import {
  addToReminderNotificationQueue,
  changedRideNotificationParticipant,
  changeReminderInQueue,
  deleteRideNotificationParticipant,
  getOrganisationNotificationSettings,
  planNotificationParticipant,
  removeFromReminderQueue,
} from '../../helpers/notification-helper';
import { dataStore, db } from 'src/app/app.component';

import { LocalCustomValidators } from 'src/app/validators/local-custom-validators';
import {
  PlanUnassignedRideComponent,
  PlanUnassignedRideDialogData,
  PlanUnassignedRideType,
} from '../plan-unassigned-ride/plan-unassigned-ride.component';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatError, MatFormField, MatLabel, MatHint, MatSuffix } from '@angular/material/form-field';
import { MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { NgTemplateOutlet } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCard } from '@angular/material/card';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatSlideToggle } from '@angular/material/slide-toggle';

export interface DialogData {
  ride: Ride;
  userToAdd?: UserInRide;
  defaultVehicle?: string;
  dates: {
    start: Date;
    end: Date;
  };
  oldRideDates?: {
    start: Date;
    end: Date;
  };
  parentRideId?: string;
  retourGenerating?: boolean;
  users: any;
  rideId: string;
  planType?: PlanType;
  retourRide?: boolean;
  unassignedRide?: boolean;
  excludeRideId?: string;
  dateUpdated?: boolean;
}

@Component({
  selector: 'app-edit-ride',
  templateUrl: './edit-ride.component.html',
  styleUrls: ['./edit-ride.component.scss'],
  imports: [
    MatDialogTitle,
    CdkDrag,
    CdkDragHandle,
    MatIconButton,
    MatTooltip,
    MatIcon,
    MatDialogClose,
    CdkScrollable,
    MatDialogContent,
    MatError,
    MatTabGroup,
    MatTab,
    NgTemplateOutlet,
    MatTabLabel,
    MatDialogActions,
    MatButton,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    MatCard,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatHint,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatSlideToggle,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatChipInput,
    MatChipListbox,
    MatChipOption,
    UserRideComponent,
  ],
})
export class EditRideComponent implements OnInit, OnDestroy {
  unsubscribes: (() => void)[] = [];
  subscriptions = new Subscription();
  // Ride identification, flags and form
  rideId: string;
  newRide: boolean;
  rideForm: UntypedFormGroup;
  retourParentId: string;
  savedRide: any; // Ride or AutomaticallyPlannedRide

  // User-related data
  users = signal<UserInRide[]>([]);
  originalUserList = signal<UserInRide[]>([]);
  timeSlots = signal<{ time: any; locations: RideLocation[] }[]>([]);
  removedUsers = signal<UserInRide[]>([]);
  addedUsers = signal<UserInRide[]>([]);
  userWarnings = signal<Record<string, any>>({});
  automaticOccupancy = signal<ReturnType<typeof calculateAutomaticRideOccupancy>>([]);
  automaticCapacityExceeded = computed(() =>
    this.automaticOccupancy().some((occupancy) => occupancy.availableSeats < 0),
  );
  automaticCapacityReached = computed(() =>
    this.automaticOccupancy().some((occupancy) => occupancy.availableSeats <= 0),
  );
  passengerTimeBaseline = new Map<
    string,
    { userPickupTime: Date; userDropoffTime: Date; rideStart: Date; rideEnd: Date }
  >();
  getUserById = getUserById;

  // Vehicle and product data
  products = signal<Product[]>([]);
  filteredRideProductOptions = signal<Product[]>([]);
  particularities: Particularity[] = dataStore.selectedOrganisation.particularities ?? [];
  allVehicles = signal<Vehicle[]>([]);
  vehicles = signal<Vehicle[]>([]);
  privateVehicleEligibilityErrors = signal<Record<string, string>>({});
  totalSeatsInAllVehicles: number = 0;
  selectedVehicle = signal<Vehicle | null>(null);
  availableSeats = signal<number | undefined>(undefined);
  seatsTaken = signal<number>(0);

  // Settings and configuration
  dataStore: DataStoreExport = dataStore;
  organisationId = dataStore.selectedOrganisation.id;
  PlanType = PlanType;
  hours = signal<number[]>([]);
  minutes = signal<{ val: number; text: string }[]>([]);
  minuteStep = 5;
  showRideProduct: boolean;
  special: boolean;
  driverId = signal<string | null>(null);
  driverName = signal<string | null>(null);
  coDriverId = signal<string | null>(null);
  coDriverName = signal<string | null>(null);
  signalColorIsPresent = false;
  activateSignalColor = false;
  ignoreDriverStatus: boolean;
  rideDuration: number;
  characteristicsStatus: boolean;
  showDoubleBookingWarning: boolean = true;
  usesDriverPlanning: boolean = dataStore.paidFunctionalities.usesDriverPlanning ?? false;
  loading = signal(true);
  retourAlreadyBooked: boolean;
  vehicleAlreadyBooked: boolean;
  checkDoubleBookingUsers: boolean = false;
  maxRideMinutesExceeded = signal<boolean>(false);
  saving = signal(false);
  selectedTabIndex: number = 0;
  timeHasPassed = signal<boolean>(false);
  rideReadOnly = signal<boolean>(false);
  preserveHistoricalVehicleAssignment = signal(false);
  moment = moment;
  Object = Object;
  onMapsClick = onMapsClick;

  // View Switcher (Passenger Tab)
  showPassengerForm = signal(false);
  selectedUserForForm = signal<UserInRide | null>(null);
  editingUserIndex = signal<number | null>(null);
  searchCtrl = new UntypedFormControl();
  allPassengers = signal<UserInRide[]>([]);
  filteredPassengers = signal<UserInRide[]>([]);
  filterForm: UntypedFormGroup = new UntypedFormGroup({
    filter: new UntypedFormControl(FilterOption.Name),
  });
  filterOption = FilterOption;

  // Notification settings
  organisationNotificationSettings: {
    email: NotificationsParticipants;
    sms: NotificationsParticipants;
  };

  // Characteristics
  @ViewChild('characteristicInput')
  characteristicInput: ElementRef<HTMLInputElement>;
  characteristicCtrl = new UntypedFormControl(null);
  filteredCharacteristics = signal<string[]>([]);
  characteristics = signal<string[]>([]);
  allCharacteristics = signal<string[]>([]);

  // Exceptions
  daypartExceptions = signal<DaypartException[]>([]);
  rideException: DaypartException;

  // Miscellaneous
  readonly separatorKeysCodes = [COMMA, ENTER, SPACE] as const;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private fb: UntypedFormBuilder,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<EditRideComponent>,
    private snackBar: MatSnackBar,
    @Inject(AddressValidationService) private addressValidationService: AddressValidationService,
  ) {}

  ngOnDestroy() {
    this.unsubscribes.forEach((unsubscribe) => unsubscribe());
    this.subscriptions.unsubscribe();
  }

  async ngOnInit() {
    console.log('EditRideComponent data:', this.data);
    this.getSettings();
    if (this.data.retourRide && !this.data.ride.isRetour) {
      this.retourParentId = this.data.ride.id ?? this.data.rideId;
      const rideduration = moment(this.data.ride.end).diff(moment(this.data.ride.start), 'minutes');
      this.data.ride = createRetourRide(
        this.data.ride,
        this.organisationId,
        this.data?.planType ?? 'rides',
        rideduration,
      );
    }

    if (this.data.ride.id) {
      this.rideId = this.data.ride.id;
      this.newRide = false;
    } else {
      this.newRide = true;
      this.rideId = doc(collection(db, `organisations/${this.organisationId}/rides`)).id;
    }

    const start = new Date(
      valueToDate(
        this.data.ride && this.data.ride.start
          ? this.data.ride.start
          : (this.data.dates.start ?? new Date()),
      ),
    );
    const end = new Date(
      valueToDate(
        this.data.ride && this.data.ride.end
          ? this.data.ride.end
          : (this.data.dates.end ?? new Date()),
      ),
    );

    this.rideForm = this.fb.group({
      comments: [this.data.ride.comments],
      rideDate: [
        start,
        [
          Validators.required,
          LocalCustomValidators.dateRangeValidator(
            new Date(2020, 0, 1),
            new Date(new Date().setFullYear(new Date().getFullYear() + 2)),
          ),
        ],
      ],
      start: [start, Validators.required],
      startHour: [start.getHours(), Validators.required],
      startMinute: [start.getMinutes(), Validators.required],
      end: [end, Validators.required],
      endHour: [end.getHours(), Validators.required],
      endMinute: [end.getMinutes(), Validators.required],
      rideProduct: [, [Validators.required, this.rideProductValidator()]],
      special: [{ value: this.data.ride.special, disabled: !this.signalColorIsPresent }],
      vehicleId: [
        ,
        [
          ...(!this.data.ride.automaticallyPlanned
            ? [Validators.required, this.seatsValidator()]
            : []),
          this.privateVehicleEligibilityValidator(),
        ],
      ],
    });
    if (moment(start).isBefore(moment(), 'day')) {
      this.rideForm.controls['rideDate'].markAsTouched();
    }

    this.timeHasPassed.set(moment(start).isBefore(moment()) || moment(end).isBefore(moment()));

    if (this.rideReadOnly()) {
      this.rideForm.disable();
      this.searchCtrl.disable();
      this.filterForm.disable();
      Object.keys(this.rideForm.controls).forEach((key) => {
        this.rideForm.get(key).setErrors(null);
      });
    }

    if (this.data.ride && this.data.ride.users) {
      this.users.set(Object.values(this.data.ride.users));
    }

    this.setupPassengerSearch();

    for (const [index, user] of this.users().entries()) {
      user.userPickupTime = user.userPickupTime ?? start;
      user.userDropoffTime = user.userDropoffTime ?? end;

      if (!user.title) {
        this.users.update((users) => {
          users.splice(index, 1);
          return [...users];
        });
      } else {
        const originalUser = structuredClone({
          ...user,
          userPickupTime: valueToDate(user.userPickupTime),
          userDropoffTime: valueToDate(user.userDropoffTime),
        });
        if (this.data.ride.isRetour && !this.data.ride.retourParentId) {
          continue;
        }
        this.originalUserList.update((list) => [...list, originalUser]);
      }
    }
    this.setPassengerTimeBaseline(start, end);

    await this.getProducts();
    await this.getVehicles();
    await this.getCharacteristics();
    await this.getDaypartExceptions();

    if (this.data.ride?.locationsOrder?.length > 0) {
      this.timeSlots.set(getTimeSlots(this.data.ride.locationsOrder ?? []));
      this.updateAutomaticOccupancy();
      if (this.data.ride?.automaticallyPlanned) {
        for (const user of this.users()) {
          this.handleTimeSlots(user);
        }
      }
      this.updateSeats();
      this.rideForm.updateValueAndValidity();
      await this.getUserWarnings();
    }
    await this.dateUpdated('date');

    if (this.data.userToAdd) {
      this.selectedTabIndex = 0;
      const newUser = { ...this.data.userToAdd };
      if (!this.data.userToAdd.rideProduct) {
        const product = this.products().find((product) => {
          return product.default;
        });
        if (product) {
          newUser.rideProduct = product;
        }
      }
      this.changeUser(null, newUser);
    }

    if (this.data.dateUpdated) {
      if (this.data.oldRideDates?.start && this.data.oldRideDates?.end) {
        const oldStart = valueToDate(this.data.oldRideDates.start);
        const oldEnd = valueToDate(this.data.oldRideDates.end);
        const startDelta = valueToDate(this.rideForm.value.start).getTime() - oldStart.getTime();
        const endDelta = valueToDate(this.rideForm.value.end).getTime() - oldEnd.getTime();
        if (startDelta !== 0 && startDelta === endDelta) {
          await this.dateUpdated('move', undefined, this.data.oldRideDates);
        } else if (!moment(oldStart).isSame(moment(this.rideForm.value.start))) {
          await this.dateUpdated('start', undefined, this.data.oldRideDates);
        }
        if (startDelta !== endDelta && !moment(oldEnd).isSame(moment(this.rideForm.value.end))) {
          await this.dateUpdated('end', undefined, this.data.oldRideDates);
        }
      } else {
        await this.dateUpdated('start');
      }
    }

    if (!this.newRide) {
      this.rideForm.updateValueAndValidity({ emitEvent: false });
      this.rideForm.markAllAsTouched();
    }

    this.loading.set(false);
  }

  async getProducts() {
    const products = (
      await getDocs(
        query(
          collection(db, `organisations/${this.organisationId}/products`),
          orderBy('name', 'asc'),
        ),
      )
    ).docs.map((doc) => ({ ...doc.data(), id: doc.id }) as Product);
    this.products.set(products);

    let product = this.products().find((product) => {
      return product.default;
    });
    if (this.data.ride.rideProduct) {
      const selectedProduct = this.products().find((product) => {
        return product.id === this.data.ride.rideProduct.id;
      });
      if (selectedProduct) {
        product = selectedProduct;
      }
    }
    if (product) {
      this.rideForm.patchValue({ rideProduct: product.name });
    }

    const sortProducts = (a: Rate, b: Rate) => {
      return a.name.localeCompare(b.name, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    };

    this.rideForm.controls.rideProduct.valueChanges.subscribe((value) => {
      const filtered = value
        ? this.products().filter((option) =>
            option.name.toLowerCase().includes(value.toLowerCase()),
          )
        : this.products().slice();
      filtered.sort(sortProducts);
      this.filteredRideProductOptions.set(filtered);
    });
    this.filteredRideProductOptions.set(this.products().slice().sort(sortProducts));
  }

  async getCharacteristics() {
    if (this.data.ride.characteristics) {
      for (const characteristics of this.data.ride.characteristics) {
        if (this.characteristics().indexOf(characteristics) === -1) {
          this.characteristics.update((chars) => [...chars, characteristics]);
        }
      }
    }

    const allCharacteristics = (
      await getDocs(
        query(
          collection(db, `organisations/${this.organisationId}/characteristics`),
          where(`active`, `==`, true),
        ),
      )
    ).docs.map((doc) => doc.data().name);
    this.allCharacteristics.set(allCharacteristics);

    this.subscriptions.add(
      this.characteristicCtrl.valueChanges.subscribe((value) => {
        if (typeof value === 'string') {
          this.filteredCharacteristics.set(
            this.allCharacteristics().filter((characteristic) =>
              characteristic.toLowerCase().includes(value.toLowerCase()),
            ),
          );
        } else {
          this.filteredCharacteristics.set([...this.allCharacteristics()]);
        }
      }),
    );
    this.filteredCharacteristics.set(this.allCharacteristics().slice());
  }

  async getVehicles() {
    this.unsubscribes.push(
      onSnapshot(
        query(
          collection(db, `organisations/${this.organisationId}/vehicles`),
          where('active', '==', true),
          orderBy('calendarStyle.sortingNumber'),
        ),
        async (snapShot) => {
          let vehicles = snapShot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as Vehicle[];
          this.preserveHistoricalVehicleAssignment.set(false);
          const rideVehicleId = this.data.ride?.vehicleId;
          const rideVehicleRef = this.data.ride?.vehicleRef;
          const vehicleRefPath =
            typeof rideVehicleRef === 'string' ? rideVehicleRef : rideVehicleRef?.path;
          const normalizedVehicleRefPath = vehicleRefPath?.replace(/^\/+/, '');
          const rideIsHistorical = moment(valueToDate(this.data.ride.start)).isBefore(moment());
          const activeVehicle = vehicles.find((vehicle) => vehicle.id === rideVehicleId);

          if (rideIsHistorical && rideVehicleId && !activeVehicle) {
            this.preserveHistoricalVehicleAssignment.set(true);
            if (normalizedVehicleRefPath) {
              try {
                const vehicleSnapshot = await getDoc(doc(db, normalizedVehicleRefPath));
                if (vehicleSnapshot.exists() && vehicleSnapshot.id === rideVehicleId) {
                  vehicles = [
                    ...vehicles,
                    { id: vehicleSnapshot.id, ...vehicleSnapshot.data() } as Vehicle,
                  ];
                }
              } catch {
                // Keep the historical assignment untouched when the fallback cannot resolve.
              }
            }
          }
          this.allVehicles.set(vehicles);
          this.totalSeatsInAllVehicles = vehicles.reduce(
            (total, vehicle) => total + vehicle.seats,
            0,
          );
          await this.updatePrivateVehicleEligibility(vehicles);

          let selectedVehicle: Vehicle | undefined;
          if ((this.data.ride && this.data.ride.vehicleId) || this.data.defaultVehicle) {
            const preferredVehicleId = this.preserveHistoricalVehicleAssignment()
              ? this.data.ride.vehicleId
              : !this.data.defaultVehicle || this.data.defaultVehicle === 'all'
                ? this.data.ride.vehicleId
                : this.data.defaultVehicle;
            selectedVehicle = this.vehicles().find((vehicle) => vehicle.id === preferredVehicleId);
          }
          if (!selectedVehicle && !this.preserveHistoricalVehicleAssignment() && this.newRide) {
            selectedVehicle = this.vehicles()[0];
          }
          if (selectedVehicle && !selectedVehicle.particularityIds) {
            selectedVehicle.particularityIds = [];
          }
          this.selectedVehicle.set(selectedVehicle ?? null);
          this.rideForm.patchValue({
            vehicleId:
              this.selectedVehicle()?.id ??
              (this.preserveHistoricalVehicleAssignment() ? this.data.ride.vehicleId : undefined),
          });

          if (this.selectedVehicle()) {
            await this.getAvailableDrivers();
            this.updateSeats();
            this.updateAutomaticOccupancy();
          }
        },
      ),
    );
  }

  async getDaypartExceptions() {
    const daypartExceptions = (
      await getDocs(collection(db, `organisations/${this.organisationId}/dayPartExceptions`))
    ).docs.map((doc) => {
      const data = doc.data() as DaypartException;
      return { id: doc.id, ...data };
    });
    this.daypartExceptions.set(daypartExceptions);
  }

  async getAvailableDrivers() {
    if (this.preserveHistoricalVehicleAssignment()) {
      return;
    }
    this.driverId.set(null);
    this.coDriverId.set(null);
    this.driverName.set(null);
    this.coDriverName.set(null);
    const vehicle = this.selectedVehicle();
    if (vehicle?.vehicleType === 'private') {
      const driverSnapshot = vehicle.driverId
        ? await getDoc(doc(db, `organisations/${this.organisationId}/users/${vehicle.driverId}`))
        : null;
      const driver = driverSnapshot?.data() as User | undefined;
      if (vehicle.driverId && driver?.roles?.includes(Role.Driver)) {
        this.driverId.set(vehicle.driverId);
        this.driverName.set(driver.name ?? null);
      } else {
        this.snackBar.open('De vaste chauffeur van dit privévoertuig is niet beschikbaar.', 'X', {
          duration: 5000,
        });
      }
      if (dataStore.generalSettings.coDriverEnabled && vehicle.coDriverId) {
        const coDriverSnapshot = await getDoc(
          doc(db, `organisations/${this.organisationId}/users/${vehicle.coDriverId}`),
        );
        const coDriver = coDriverSnapshot?.data() as User | undefined;
        if (coDriver?.roles?.includes(Role.CoDriver)) {
          this.coDriverId.set(vehicle.coDriverId);
          this.coDriverName.set(coDriver.name ?? null);
        } else {
          this.snackBar.open('De vaste bijrijder van dit privévoertuig is niet beschikbaar.', 'X', {
            duration: 5000,
          });
        }
      }
      return;
    }
    if (!this.usesDriverPlanning) {
      return;
    }
    const vehicleId = this.rideForm.get('vehicleId')?.value;
    const startValue = this.rideForm.value.start;
    if (!vehicleId || !startValue) {
      return;
    }
    const dateString = getDateString(this.rideForm.value.start);
    const startHours = this.rideForm.value.startHour;
    const startMin = this.rideForm.value.startMinute;
    const startDate = new Date(new Date(this.rideForm.value.start).setHours(startHours, startMin));

    const daypartDays = (
      await getDocs(
        query(
          collection(db, `organisations/${this.organisationId}/daypartDays`),
          where('vehicleId', '==', vehicleId),
          where('startString', '==', dateString),
        ),
      )
    ).docs;
    if (daypartDays.length < 1) {
      return;
    }

    for (const daypart of daypartDays) {
      const daypartDay = daypart.data() as DaypartDay;
      const isBetween = moment(startDate).isBetween(
        new Date(valueToDate(daypartDay.start)),
        new Date(valueToDate(daypartDay.end)),
        null,
        '[)', // '[) = equals or between instead of only between
      );
      if (isBetween) {
        const vehicle = this.selectedVehicle();
        const privateVehicle = vehicle?.vehicleType === 'private';
        this.driverId.set(privateVehicle ? (vehicle.driverId ?? null) : daypartDay.driverId);
        this.driverName.set(
          privateVehicle && daypartDay.driverId !== vehicle.driverId ? null : daypartDay.driverName,
        );
        if (dataStore.generalSettings.coDriverEnabled) {
          this.coDriverId.set(
            privateVehicle ? (vehicle.coDriverId ?? null) : daypartDay.coDriverId,
          );
          this.coDriverName.set(
            privateVehicle && daypartDay.coDriverId !== vehicle.coDriverId
              ? null
              : daypartDay.coDriverName,
          );
        }
        break;
      }
    }
  }

  getSettings() {
    this.organisationNotificationSettings = getOrganisationNotificationSettings();
    this.characteristicsStatus = dataStore.generalSettings?.characteristicsStatus ?? false;
    this.showDoubleBookingWarning =
      dataStore.generalSettings?.doubleBookingActivated !== null
        ? !dataStore.generalSettings.doubleBookingActivated
        : true;
    this.signalColorIsPresent = dataStore.generalSettings?.signalColor ? true : false;
    this.activateSignalColor = dataStore.generalSettings?.activateSignalColor ? true : false;
    this.ignoreDriverStatus = dataStore.generalSettings?.ignoreDriverStatus ?? false;
    this.showRideProduct = dataStore.generalSettings?.showRideProduct ?? true;
    this.rideDuration = dataStore.generalSettings?.customRideDuration ?? 15;

    const startHour = dataStore.generalSettings?.daypartSettings?.startHour ?? 6;
    const endHour = dataStore.generalSettings?.daypartSettings?.endHour ?? 24;
    const hours: number[] = [];
    for (let i = startHour; i <= endHour; i++) {
      hours.push(i);
    }
    this.hours.set(hours);

    const minutes: { val: number; text: string }[] = [];
    for (let i = 0; i < 60; i += this.minuteStep) {
      minutes.push({
        val: i,
        text: `0${i}`.slice(-2),
      });
    }
    this.minutes.set(minutes);
  }

  addCharacteristic(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      const indexAll = this.allCharacteristics().indexOf(value);
      if (indexAll >= 0) {
        const index = this.characteristics().indexOf(value);
        if (index === -1) {
          this.characteristics.update((chars) => [...chars, value]);
        }
      }
    }
    event.value = '';
    this.characteristicCtrl.setValue(null);
  }

  removeCharacteristic(characteristic: string): void {
    const index = this.characteristics().indexOf(characteristic);
    if (index >= 0) {
      this.characteristics.update((chars) => {
        const newChars = [...chars];
        newChars.splice(index, 1);
        return newChars;
      });
    }
  }

  selectedCharacteristic(event: MatAutocompleteSelectedEvent): void {
    if (this.characteristics().indexOf(event.option.viewValue) === -1) {
      this.characteristics.update((chars) => [...chars, event.option.viewValue]);
    }
    this.characteristicInput.nativeElement.value = '';
    this.characteristicCtrl.setValue(null);
  }

  async dateUpdated(
    type: 'start' | 'end' | 'date' | 'move',
    newDate?: Date,
    previousRideDates?: { start: Date; end: Date },
  ) {
    const previousStart = valueToDate(previousRideDates?.start ?? this.rideForm.value.start);
    const previousEnd = valueToDate(previousRideDates?.end ?? this.rideForm.value.end);
    const patchObj = {};
    if (type === 'start') {
      const startDate = new Date(this.rideForm.value.start);
      startDate.setHours(this.rideForm.value.startHour, this.rideForm.value.startMinute);
      if (startDate >= this.rideForm.value.end) {
        const endDate = new Date(this.rideForm.value.start);
        endDate.setHours(
          this.rideForm.value.startHour,
          this.rideForm.value.startMinute + this.rideDuration,
        );
        patchObj['end'] = endDate;
        patchObj['endHour'] = endDate.getHours();
        patchObj['endMinute'] = endDate.getMinutes();
        this.snackBar.open(
          'De einddatum is aangepast, omdat hij eerder was dan de begindatum.',
          'X',
          {
            duration: 5000,
          },
        );
      }
      patchObj['start'] = startDate;
    } else if (type === 'end') {
      const endDate = new Date(this.rideForm.value.end);
      endDate.setHours(this.rideForm.value.endHour, this.rideForm.value.endMinute);
      patchObj['end'] = endDate;
      if (this.rideForm.value.start >= endDate) {
        const endDate = new Date(this.rideForm.value.start);
        endDate.setHours(
          this.rideForm.value.startHour,
          this.rideForm.value.startMinute + this.rideDuration,
        );
        patchObj['end'] = endDate;
        patchObj['endHour'] = endDate.getHours();
        patchObj['endMinute'] = endDate.getMinutes();
        this.snackBar.open(
          'De gekozen einddatum is aangepast, omdat hij eerder was dan de begindatum.',
          'X',
          {
            duration: 5000,
          },
        );
      }
    } else if (type === 'date' && newDate) {
      const startDate = new Date(newDate);
      const endDate = new Date(newDate);
      startDate.setHours(this.rideForm.value.startHour, this.rideForm.value.startMinute);
      endDate.setHours(this.rideForm.value.endHour, this.rideForm.value.endMinute);
      patchObj['start'] = startDate;
      patchObj['end'] = endDate;

      for (const user of this.users()) {
        const pickupBase = new Date(newDate);
        const pickupTime = valueToDate(user.userPickupTime);
        pickupBase.setHours(pickupTime.getHours(), pickupTime.getMinutes());
        user.userPickupTime = pickupBase;

        const dropoffBase = new Date(newDate);
        const dropoffTime = valueToDate(user.userDropoffTime);
        dropoffBase.setHours(dropoffTime.getHours(), dropoffTime.getMinutes());
        user.userDropoffTime = dropoffBase;
      }
      for (const timeSlot of this.timeSlots()) {
        const slotBase = new Date(newDate);
        const slotTime = valueToDate(timeSlot.time);
        slotBase.setHours(slotTime.getHours(), slotTime.getMinutes());
        timeSlot.time = slotBase;
        for (const location of timeSlot.locations) {
          const locBase = new Date(newDate);
          const locTime = valueToDate(location.time);
          locBase.setHours(locTime.getHours(), locTime.getMinutes());
          location.time = locBase;
        }
      }
    }
    await this.getAvailableDrivers();
    this.rideForm.patchValue(patchObj);
    await this.updatePrivateVehicleEligibility();

    const finalStart = patchObj['start'] ? patchObj['start'] : this.rideForm.value.start;
    const finalEnd = patchObj['end'] ? patchObj['end'] : this.rideForm.value.end;
    // Update user pickup and dropoff times
    if (type === 'start' || type === 'end' || type === 'move') {
      if (this.data.ride?.automaticallyPlanned) {
        const finalStartDate = valueToDate(finalStart);
        const finalEndDate = valueToDate(finalEnd);
        const maxPassengerRideMinutes =
          this.dataStore.generalSettings.maxPassengerRideMinutes ?? 45;
        const maxPassengerRideMilliseconds = maxPassengerRideMinutes * 60 * 1000;
        const minPassengerRideMilliseconds = 60 * 1000;
        for (const user of this.users()) {
          let baseline = this.passengerTimeBaseline.get(user.id);
          if (!baseline) {
            baseline = {
              userPickupTime: new Date(valueToDate(user.userPickupTime)),
              userDropoffTime: new Date(valueToDate(user.userDropoffTime)),
              rideStart: previousStart,
              rideEnd: previousEnd,
            };
            this.passengerTimeBaseline.set(user.id, baseline);
          }
          if (type === 'move') {
            const delta = finalStartDate.getTime() - previousStart.getTime();
            user.userPickupTime = new Date(baseline.userPickupTime.getTime() + delta);
            user.userDropoffTime = new Date(baseline.userDropoffTime.getTime() + delta);
          } else {
            const pickupDelta = finalStartDate.getTime() - baseline.rideStart.getTime();
            const dropoffDelta = finalEndDate.getTime() - baseline.rideEnd.getTime();
            let newPickupTime = new Date(baseline.userPickupTime.getTime() + pickupDelta);
            let newDropoffTime = new Date(baseline.userDropoffTime.getTime() + dropoffDelta);
            if (newDropoffTime.getTime() - newPickupTime.getTime() > maxPassengerRideMilliseconds) {
              if (type === 'start') {
                newPickupTime = new Date(newDropoffTime.getTime() - maxPassengerRideMilliseconds);
              } else {
                newDropoffTime = new Date(newPickupTime.getTime() + maxPassengerRideMilliseconds);
              }
            }
            if (newPickupTime >= newDropoffTime) {
              if (type === 'start') {
                newPickupTime = new Date(newDropoffTime.getTime() - minPassengerRideMilliseconds);
              } else {
                newDropoffTime = new Date(newPickupTime.getTime() + minPassengerRideMilliseconds);
              }
            }
            user.userPickupTime = newPickupTime;
            user.userDropoffTime = newDropoffTime;
          }
          this.handleTimeSlots(user);
        }
        if (type === 'move') {
          this.setPassengerTimeBaseline(finalStartDate, finalEndDate);
        }
      } else {
        for (const user of this.users()) {
          user.userPickupTime = finalStart;
          user.userDropoffTime = finalEnd;
          this.handleTimeSlots(user);
        }
      }
    }

    this.rideForm.controls.rideDate.updateValueAndValidity();
    this.timeHasPassed.set(
      moment(this.rideForm.value.start).isBefore(moment()) ||
        moment(this.rideForm.value.end).isBefore(moment()),
    );
    this.updateAutomaticOccupancy();
    await this.getUserWarnings();
  }

  private async updatePrivateVehicleEligibility(
    candidateVehicles = this.allVehicles(),
  ): Promise<void> {
    if (
      this.preserveHistoricalVehicleAssignment() ||
      this.data.ride?.finished === true ||
      moment(this.rideForm.value.end).isBefore(moment())
    ) {
      this.privateVehicleEligibilityErrors.set({});
      this.vehicles.set(candidateVehicles);
      return;
    }

    const rideStart = new Date(valueToDate(this.rideForm.value.start));
    rideStart.setHours(this.rideForm.value.startHour, this.rideForm.value.startMinute, 0, 0);
    const errors: Record<string, string> = {};
    await Promise.all(
      candidateVehicles
        .filter((vehicle) => vehicle.vehicleType === 'private')
        .map(async (vehicle) => {
          if (!vehicle.driverId) {
            errors[vehicle.id!] = 'Dit privévoertuig heeft geen vaste chauffeur.';
            return;
          }
          const daypartDays = (
            await getDocs(
              query(
                collection(db, `organisations/${this.organisationId}/daypartDays`),
                where('vehicleId', '==', vehicle.id),
                where('startString', '==', getDateString(rideStart)),
              ),
            )
          ).docs;
          const assignmentMatches = await Promise.all(
            daypartDays.map(async (daypartDayDoc) => {
              const daypartDay = daypartDayDoc.data() as DaypartDay;
              const hasAssignedDriver = !!daypartDay.driverId;
              if (!hasAssignedDriver || !daypartDay.daypartId) {
                return false;
              }
              const assignmentStart = valueToDate(daypartDay.start);
              const assignmentEnd = valueToDate(daypartDay.end);
              const startsWithinAssignment =
                !!assignmentStart && !!assignmentEnd && rideStart >= assignmentStart;
              const endsAfterAssignment = !!assignmentEnd && rideStart < assignmentEnd;
              const matches = startsWithinAssignment && endsAfterAssignment;
              return matches;
            }),
          );
          const hasMatchingAssignment = assignmentMatches.some(Boolean);
          if (!hasMatchingAssignment) {
            errors[vehicle.id] = 'Dit privévoertuig is niet ingepland voor dit tijdstip.';
          }
        }),
    );
    this.privateVehicleEligibilityErrors.set(errors);
    const retainedVehicleId =
      this.selectedVehicle()?.id ??
      (!this.data.defaultVehicle || this.data.defaultVehicle === 'all'
        ? this.data.ride?.vehicleId
        : this.data.defaultVehicle);
    this.vehicles.set(
      candidateVehicles.filter(
        (vehicle) => !errors[vehicle.id ?? ''] || vehicle.id === retainedVehicleId,
      ),
    );
    this.rideForm.controls.vehicleId.updateValueAndValidity();
  }

  getPrivateVehicleEligibilityError(): string | null {
    const vehicleId = this.selectedVehicle()?.id;
    return vehicleId ? (this.privateVehicleEligibilityErrors()[vehicleId] ?? null) : null;
  }

  setPassengerTimeBaseline(rideStart: Date, rideEnd: Date) {
    this.passengerTimeBaseline.clear();
    for (const user of this.users()) {
      if (!user.id || !user.userPickupTime || !user.userDropoffTime) {
        continue;
      }
      this.passengerTimeBaseline.set(user.id, {
        userPickupTime: new Date(valueToDate(user.userPickupTime)),
        userDropoffTime: new Date(valueToDate(user.userDropoffTime)),
        rideStart: new Date(valueToDate(rideStart)),
        rideEnd: new Date(valueToDate(rideEnd)),
      });
    }
  }

  async vehicleChanged() {
    if (this.preserveHistoricalVehicleAssignment()) {
      this.rideForm.controls.vehicleId.setValue(this.data.ride.vehicleId, { emitEvent: false });
      this.selectedVehicle.set(
        this.vehicles().find((vehicle) => vehicle.id === this.data.ride.vehicleId) ?? null,
      );
      return;
    }
    this.selectedVehicle.set(
      this.vehicles().find((vehicle) => {
        return vehicle['id'] === this.rideForm.value.vehicleId;
      }) || null,
    );
    await this.getAvailableDrivers();
    this.updateSeats();
    this.updateAutomaticOccupancy();
    await this.getUserWarnings();
  }

  updateAutomaticOccupancy() {
    if (!this.data.ride?.automaticallyPlanned || !this.selectedVehicle()) {
      this.automaticOccupancy.set([]);
      return;
    }

    const locations = this.timeSlots().flatMap((timeSlot) => timeSlot.locations);
    this.automaticOccupancy.set(
      calculateAutomaticRideOccupancy(
        locations,
        this.selectedVehicle().seats,
        this.coDriverId(),
        this.users(),
        this.particularities,
      ),
    );
    this.updatePassengerSearchAvailability();
  }

  getAutomaticOccupancyForUsers(users: UserInRide[]) {
    const locations: RideLocation[] = [];
    for (const user of users) {
      locations.push(
        {
          userId: user.id,
          type: 'from',
          time: valueToDate(user.userPickupTime),
          location: user.from,
          fullLocation: getFullLocationString(user.from),
        },
        {
          userId: user.id,
          type: 'to',
          time: valueToDate(user.userDropoffTime),
          location: user.to,
          fullLocation: getFullLocationString(user.to),
        },
      );
    }

    return calculateAutomaticRideOccupancy(
      locations,
      this.selectedVehicle()?.seats ?? 0,
      this.coDriverId(),
      users,
      this.particularities,
    );
  }

  getUserSeatWeight(user: UserInRide) {
    return (
      1 +
      this.particularities.filter(
        (particularity) =>
          particularity.takesSeat && user.particularityIds?.includes(particularity.id),
      ).length
    );
  }

  updateSeats() {
    if (this.preserveHistoricalVehicleAssignment() && !this.selectedVehicle()) {
      this.availableSeats.set(this.data.ride?.availableSeats);
      this.rideForm.controls.vehicleId.updateValueAndValidity();
      this.rideForm.controls.vehicleId.markAsTouched();
      this.updatePassengerSearchAvailability();
      return;
    }

    const totalSeats = this.selectedVehicle()?.seats ?? 0;
    let seatsTaken = 0;
    this.users().forEach((user) => {
      seatsTaken = seatsTaken + 1;
      if (this.particularities && this.particularities.length === 0) {
        return;
      }
      this.particularities.forEach((icon) => {
        if (icon.takesSeat && user?.particularityIds.includes(icon.id)) {
          seatsTaken = seatsTaken + 1;
        }
      });
    });
    if (this.coDriverId()) {
      seatsTaken = seatsTaken + 1;
    }
    this.seatsTaken.set(seatsTaken);
    this.availableSeats.set(totalSeats - this.seatsTaken());
    this.rideForm.controls.vehicleId.updateValueAndValidity();
    this.rideForm.controls.vehicleId.markAsTouched();
    this.updatePassengerSearchAvailability();
  }

  updatePassengerSearchAvailability() {
    if (!this.rideReadOnly()) {
      // Revisit this when passenger pickup/drop-off times become directly editable.
      const capacityReached = this.data.ride?.automaticallyPlanned
        ? this.automaticCapacityReached()
        : (this.availableSeats() ?? 0) <= 0;
      if (capacityReached) {
        this.searchCtrl.disable();
        this.filterForm.disable();
      } else {
        this.searchCtrl.enable();
        this.filterForm.enable();
      }
    }
  }

  moveUser(user: UserInRide) {
    this.dialog
      .open(WarningDialog, {
        width: '600px',
        data: { dialogType: 'movePassengerImmediateSave' },
      })
      .afterClosed()
      .subscribe((warningResult) => {
        if (warningResult) {
          this.dialog
            .open(PlanUnassignedRideComponent, {
              width: '500px',
              data: {
                passenger: user,
                start: user.userPickupTime,
                end: user.userDropoffTime,
                rideProduct: user.rideProduct,
                type: PlanUnassignedRideType.Move,
                excludeRideId: this.rideId,
              } as PlanUnassignedRideDialogData,
            })
            .afterClosed()
            .subscribe(async (result) => {
              if (result) {
                this.removeUser(this.users().indexOf(user));

                // Immediately persist the user removal to database
                await this.removeUserFromRide(user);
                this.dialog.open(WarningDialog, {
                  width: '600px',
                  data: { dialogType: 'movePassengerDone' },
                });
              }
            });
        }
      });
  }

  changeUser(i?: number, user?: UserInRide) {
    this.editingUserIndex.set(i ?? null);
    this.selectedUserForForm.set(user ?? ({} as UserInRide));
    this.showPassengerForm.set(true);
  }

  async onUserFormAdded(result: UserInRide) {
    const i = this.editingUserIndex();
    const newUser = i === null;

    if (newUser && !result.phoneNumber) {
      result.phoneNumber = result.phone ?? '';
    }

    const existingUser = i === null ? null : this.users()[i];
    const hasAdditionalWeight =
      existingUser !== null &&
      this.getUserSeatWeight(result) > this.getUserSeatWeight(existingUser);

    if (
      this.data.ride?.automaticallyPlanned &&
      this.selectedVehicle() &&
      (newUser || hasAdditionalWeight)
    ) {
      const candidateUsers = this.users().filter((_, index) => index !== i);
      candidateUsers.push(result);
      const candidateOccupancy = this.getAutomaticOccupancyForUsers(candidateUsers);
      if (candidateOccupancy.some((occupancy) => occupancy.availableSeats < 0)) {
        this.snackBar.open(
          `${newUser ? 'Passagier niet toegevoegd' : 'Passagier kon niet worden bewerkt'}. Onvoldoende zitplaatsen beschikbaar.`,
          'X',
          { duration: 6000 },
        );
        return;
      }
    }

    if (newUser) {
      this.addedUsers.update((users) => [...users, result]);
      this.users.update((users) => [...users, result]);
    } else {
      this.users.update((users) => {
        users[i] = result;
        return [...users];
      });
    }

    this.handleTimeSlots(result);
    this.updateSeats();
    this.updateAutomaticOccupancy();
    await this.getUserWarnings();
    await this.getAvailableDrivers();
    this.updateAutomaticOccupancy();
    this.showPassengerForm.set(false);
    this.selectedUserForForm.set(null);
    this.editingUserIndex.set(null);
    this.searchCtrl.setValue(null);
    this.filterForm.patchValue({ filter: FilterOption.Name });
  }

  onUserFormCanceled() {
    this.showPassengerForm.set(false);
    this.selectedUserForForm.set(null);
    this.editingUserIndex.set(null);
    this.searchCtrl.setValue(null);
    this.filterForm.patchValue({ filter: FilterOption.Name });
    if (this.data.unassignedRide && this.users().length === 0) {
      this.dialogRef.close();
    }
  }

  getUserRideDialogData() {
    return {
      user: this.selectedUserForForm() ?? {},
      users: this.users(),
      dialogStart: this.rideForm.value.start,
      dialogEnd: this.rideForm.value.end,
      particularities: this.particularities,
      rideDates: { start: this.rideForm.value.start, end: this.rideForm.value.end },
      planType: this.data.planType,
      rideId: !this.newRide ? (this.data.ride?.id ?? null) : null,
      excludeRideId: this.data.excludeRideId,
      forceRideStartEnd: true,
    } as any;
  }

  setupPassengerSearch() {
    this.unsubscribes.push(
      onSnapshot(
        query(
          collection(db, `organisations/${this.organisationId}/users`),
          where(`passenger`, `==`, true),
        ),
        (snapshot) => {
          this.allPassengers.set(
            snapshot.docs.map((doc) => {
              const user = { id: doc.id, ...doc.data() } as UserInRide;
              if (!user.credits) {
                user.credits = 0;
              }
              return user;
            }),
          );
        },
      ),
    );

    this.subscriptions.add(
      this.searchCtrl.valueChanges.subscribe((value) => {
        if (!value || value === '' || typeof value === 'object') {
          this.filteredPassengers.set([]);
          return;
        }
        const filtered = this.allPassengers().filter((user) =>
          this.checkSearchFilters(user, value),
        );
        this.filteredPassengers.set(filtered.slice(0, 10)); // Limit results
      }),
    );
  }

  checkSearchFilters(item: any, searchQuery: string) {
    searchQuery = searchQuery.toLowerCase();
    const filterType = this.filterForm.value.filter;
    const isSuperUser = dataStore.rootUser?.rights === 'admin';

    if (item.hidden || (item.superuser && !isSuperUser)) {
      return false;
    }

    switch (filterType) {
      case FilterOption.Name:
        return item.name?.toLowerCase().includes(searchQuery);
      case FilterOption.Address:
        return (
          item.address?.toLowerCase().includes(searchQuery) ||
          item.postal?.toLowerCase().includes(searchQuery) ||
          item.streetName?.toLowerCase().includes(searchQuery)
        );
      case FilterOption.Email:
        return item.email?.toLowerCase().includes(searchQuery);
      case FilterOption.PhoneNumber:
        return (
          item.phoneNumber?.toLowerCase().includes(searchQuery) ||
          item.phone?.toLowerCase().includes(searchQuery)
        );
      default:
        return false;
    }
  }

  setSearchFilter(option: FilterOption) {
    this.filterForm.controls.filter.setValue(option);
    this.searchCtrl.setValue(this.searchCtrl.value); // Trigger re-filter
  }

  preventChipUnselect(event: MatChipSelectionChange) {
    if (!event.selected) {
      event.source.select();
    }
  }

  removeUserConfirm(index: number) {
    const dialogRef = this.dialog.open(WarningDialog, {
      width: '600px',
      data: {
        dialogType: 'removePassengerFromRide',
        optionalData: { passengerName: this.users()[index].title },
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.removeUser(index);
      }
    });
  }

  removeUser(index: number) {
    const removedUser = this.users()[index];
    this.users.update((users) => users.filter((_, i) => i !== index));
    this.removedUsers.update((users) => [...users, removedUser]);
    this.userWarnings.update((warnings) => {
      const newWarnings = { ...warnings };
      delete newWarnings[removedUser.id];
      return newWarnings;
    });
    const timeSlots = [];
    for (const timeSlot of this.timeSlots()) {
      timeSlot.locations = timeSlot.locations.filter((loc) => loc.userId !== removedUser.id);
      if (timeSlot.locations.length > 0) {
        timeSlots.push(timeSlot);
      }
    }
    this.timeSlots.set(timeSlots);
    this.updateAutomaticOccupancy();
    this.updateSeats();
  }

  async removeUserFromRide(user: UserInRide) {
    if (!this.rideId || this.newRide) {
      return;
    }

    const rideRef = doc(db, `organisations/${this.organisationId}/rides/${this.rideId}`);

    // Build new locationsOrder without this user
    let newLocationsOrder = [];
    this.timeSlots().forEach((timeSlot) => {
      newLocationsOrder = newLocationsOrder.concat(timeSlot.locations);
    });

    const usersObj = {};
    this.users().forEach((u: any) => {
      usersObj[u.id] = u;
    });

    const rideData = {
      ...this.data.ride,
      users: usersObj as Map<string, any>,
      locationsOrder: newLocationsOrder,
      availableSeats: this.availableSeats(),
      comments: this.data.ride?.comments,
    } as Ride;

    const updatedTitle = getRideTitle(rideData);

    // Build update object
    const updateObj = {
      [`users.${user.id}`]: deleteField(),
      userIds: this.users().map((u) => u.id),
      locationsOrder: newLocationsOrder,
      availableSeats: this.availableSeats(),
      title: updatedTitle,
    };

    await updateDoc(rideRef, updateObj);
  }

  handleTimeSlots(user: UserInRide) {
    const locationPoints = [
      { type: 'from', time: user.userPickupTime },
      { type: 'to', time: user.userDropoffTime },
    ];

    for (const data of locationPoints) {
      let currentTimeSlotData;
      this.timeSlots().find((timeSlot, index) => {
        const locationIndex = timeSlot.locations.findIndex(
          (loc) => loc.userId === user.id && loc.type === data.type,
        );
        if (locationIndex !== -1) {
          currentTimeSlotData = {
            timeSlot: timeSlot,
            locationIndex: locationIndex,
            timeSlotIndex: index,
          };
        }
      });
      const newLocation: RideLocation = {
        userId: user.id,
        type: data.type,
        time: valueToDate(data.time),
        location: user[data.type],
        fullLocation: getFullLocationString(user[data.type]),
      };
      const newLocationTimeslot = this.timeSlots().find((timeSlot) => {
        return moment(timeSlot.time).isSame(moment(newLocation.time), 'minute');
      });

      if (currentTimeSlotData) {
        const timeSlotRef = this.timeSlots()[currentTimeSlotData.timeSlotIndex];
        if (
          !moment(
            valueToDate(
              currentTimeSlotData.timeSlot.locations[currentTimeSlotData.locationIndex].time,
            ),
          ).isSame(moment(newLocation.time))
        ) {
          timeSlotRef.locations.splice(currentTimeSlotData.locationIndex, 1);
          if (newLocationTimeslot) {
            newLocationTimeslot.locations.push(newLocation);
          } else {
            this.timeSlots.update((slots) => [
              ...slots,
              { time: newLocation.time, locations: [newLocation] },
            ]);
          }
        } else {
          timeSlotRef.locations[currentTimeSlotData.locationIndex] = newLocation;
        }
      } else {
        if (newLocationTimeslot) {
          newLocationTimeslot.locations.push(newLocation);
        } else {
          this.timeSlots.update((slots) => [
            ...slots,
            { time: newLocation.time, locations: [newLocation] },
          ]);
        }
      }
    }

    this.timeSlots.set(
      this.timeSlots()
        .filter((timeSlot) => timeSlot.locations.length > 0)
        .sort((a, b) => {
          return moment(a.time).diff(moment(b.time));
        }),
    );
  }

  moveOrder(direction: 'up' | 'down', timeSlotIndex: number, locationIndex: number) {
    let newIndex = locationIndex;
    if (direction === 'up') {
      newIndex--;
    }
    if (direction === 'down') {
      newIndex++;
    }
    this.timeSlots.update((slots) => {
      const newSlots = [...slots];
      moveItemInArray(newSlots[timeSlotIndex].locations, locationIndex, newIndex);
      return newSlots;
    });
    this.updateAutomaticOccupancy();
  }

  async getUserWarnings(checkDoubleBooking: boolean = false) {
    const userWarnings: Record<string, Record<string, string>> = {};
    for (const timeSlot of this.timeSlots()) {
      for (const location of timeSlot.locations) {
        if (!userWarnings[location.userId]) {
          userWarnings[location.userId] = {};
        }
        if (location.type === 'from') {
          if (moment(location.time).isBefore(moment(this.rideForm.value.start))) {
            userWarnings[location.userId]['from'] =
              'De ophaaltijd voor deze passagier vind plaats voor de start van de rit';
          } else if (moment(location.time).isAfter(moment(this.rideForm.value.end))) {
            userWarnings[location.userId]['from'] =
              'De ophaaltijd voor deze passagier vind plaats na het einde van de rit';
          }
        } else if (location.type === 'to') {
          if (moment(location.time).isBefore(moment(this.rideForm.value.start))) {
            userWarnings[location.userId]['to'] =
              'De afzettijd voor deze passagier vind plaats voor de start van de rit';
          } else if (moment(location.time).isAfter(moment(this.rideForm.value.end))) {
            userWarnings[location.userId]['to'] =
              'De afzettijd voor deze passagier vind plaats na het einde van de rit';
          }
        }

        const user = this.getUserById(this.users(), location.userId);
        if (user && user?.particularityIds?.length > 0 && this.selectedVehicle()) {
          const missingParticularities = [];
          for (const particularityId of user.particularityIds) {
            if (!this.selectedVehicle().particularityIds) {
              this.selectedVehicle().particularityIds = [];
            }
            if (!this.selectedVehicle().particularityIds.includes(particularityId)) {
              missingParticularities.push(
                this.particularities.find((p) => p.id === particularityId)?.name,
              );
            }
          }
          if (missingParticularities.length > 0) {
            const particularityString = missingParticularities.join(', ');
            userWarnings[location.userId]['particularities'] =
              `De passagier kan niet mee met het gekozen voertuig vanwege de volgende bijzonderheden: ${particularityString}`;
          }
        }
      }
    }

    for (const user of this.users()) {
      if (!userWarnings[user.id]) {
        userWarnings[user.id] = {};
      }

      const start = moment(valueToDate(user.userPickupTime));
      const end = moment(valueToDate(user.userDropoffTime));

      if (start.isSameOrAfter(end)) {
        userWarnings[user.id]['invalidTimeCombination'] =
          'Het ophaalmoment moet plaatsvinden voor het afzetmoment.';
      }

      const duration = end.diff(start, 'minutes');
      const maxMinutes = this.dataStore.generalSettings.maxPassengerRideMinutes ?? 45;
      if (duration > maxMinutes) {
        userWarnings[user.id]['maxRideMinutesExceeded'] =
          `De rit van deze passagier mag niet langer dan ${maxMinutes} minuten duren.`;
      }
    }

    if (checkDoubleBooking && this.showDoubleBookingWarning) {
      for (const user of this.users()) {
        const rideDates = {
          start: moment(this.rideForm.value.start),
          end: moment(this.rideForm.value.end),
        };
        const doubleBooked = await checkUserDoubleBooking(
          user,
          rideDates,
          this.data.ride.id,
          this.organisationId,
          this.data.excludeRideId,
        );
        if (doubleBooked) {
          userWarnings[user.id]['doubleBooking'] =
            'Deze passagier zit al in een andere rit tijdens deze periode';
        }
      }
    }

    for (const warning of Object.keys(userWarnings)) {
      if (Object.keys(userWarnings[warning]).length === 0) {
        delete userWarnings[warning];
      }
    }

    this.userWarnings.set(userWarnings);
  }

  async deleteRide() {
    this.saving.set(true);
    let notificationBool = false;
    if (this.users().length > 0) {
      notificationBool =
        (this.organisationNotificationSettings.email.enabled &&
          this.organisationNotificationSettings.email.RideDeleted) ||
        (this.organisationNotificationSettings.sms.enabled &&
          this.organisationNotificationSettings.sms.RideDeleted);
    }
    const noDeleteReason = dataStore.generalSettings.noDeleteReason ?? false;

    this.dialog
      .open(DeleteRideComponent, {
        width: '600px',
        data: {
          notification: notificationBool,
          noDeleteReason: noDeleteReason,
        },
      })
      .afterClosed()
      .subscribe(async (deleteResult: any) => {
        if (deleteResult !== false) {
          const rideRef = doc(db, `organisations/${this.organisationId}/rides/${this.rideId}`);
          console.log('Deleting ride:', rideRef.path);
          if (this.data.ride.parentRideId) {
            const updateRef = this.dialog.open(CopyUpdateComponent, {
              width: '600px',
              data: {
                oldRide: structuredClone(this.data.ride),
                delete: true,
                deleteReason: deleteResult,
              },
            });
            updateRef.afterClosed().subscribe(async (result) => {
              if (result === undefined) {
                this.saving.set(false);
                return;
              }

              await this.backupRide(deleteResult);
              await deleteDoc(rideRef);
              this.notifyParticipantsRideDeleted();

              if (result) {
                this.snackBar.open('De ritten zijn verwijderd.', 'X', {
                  duration: 5000,
                });
              } else if (result === false) {
                this.snackBar.open('De rit is verwijderd.', 'X', {
                  duration: 5000,
                });
              }
            });
          } else {
            await this.backupRide(deleteResult);
            await deleteDoc(rideRef);
            this.notifyParticipantsRideDeleted();
            this.snackBar.open('De rit is verwijderd.', 'X', {
              duration: 5000,
            });
          }
        } else {
          this.saving.set(false);
        }
      });
  }

  async backupRide(deleteResult: string) {
    const backupRide = {} as any;
    backupRide.date = new Date();
    backupRide.doneByUser = dataStore.rootUser.email;
    backupRide.doneByUid = dataStore.rootUser.id;
    backupRide.deleteReason = deleteResult;
    backupRide.rideData = { ...this.data.ride };
    console.log('Backing up ride:', backupRide);
    console.log(
      'backupRideRef',
      `eventLogs/${this.organisationId}/deletedRides/${this.data.ride.id}`,
    );
    await setDoc(
      doc(db, `eventLogs/${this.organisationId}/deletedRides/${this.data.ride.id}`),
      backupRide,
    );
    const editDocs = await getDocs(
      collection(db, `organisations/${this.organisationId}/rides/${this.data.ride.id}/edits`),
    );
    editDocs.forEach((editDoc) => {
      setDoc(
        doc(
          db,
          `eventLogs/${this.organisationId}/deletedRides/${this.data.ride.id}/edits/${editDoc.id}`,
        ),
        editDoc.data(),
      );
      deleteDoc(editDoc.ref);
    });
    const scanDocs = await getDocs(
      collection(db, `organisations/${this.organisationId}/rides/${this.data.ride.id}/scans`),
    );
    scanDocs.forEach((scanDoc) => {
      deleteDoc(scanDoc.ref);
    });
  }

  async save() {
    if (this.saving()) {
      return;
    }
    if (this.rideReadOnly()) {
      return;
    }
    if (this.timeHasPassed()) {
      const result = await firstValueFrom(
        this.dialog
          .open(WarningDialog, {
            width: '500px',
            data: {
              dialogType: 'rideInPast',
            } as GenericWarningDialogData,
          })
          .afterClosed(),
      );
      if (!result) {
        return;
      }
    }
    // if (this.timeHasPassed || this.rideReadOnly) {
    //   return;
    // }
    this.saving.set(true);
    if (this.users().length === 0) {
      if (this.newRide) {
        const result = await firstValueFrom(
          this.dialog
            .open(WarningDialog, {
              width: '500px',
              data: {
                dialogType: 'saveEmptyRide',
              } as GenericWarningDialogData,
            })
            .afterClosed(),
        );
        if (!result) {
          this.saving.set(false);
          return;
        }
      } else {
        const result = await firstValueFrom(
          this.dialog
            .open(WarningDialog, {
              width: '500px',
              data: {
                dialogType: 'saveEmptyRideOrDelete',
                optionalData: {
                  confirmButtonText: 'Opslaan als lege rit',
                  confirmButtonValue: 'save',
                  cancelButtonText: 'Annuleren',
                  thirdButtonText: 'Verwijderen',
                  thirdButtonValue: 'delete',
                  thirdButtonClass: 'error-color',
                  thirdButtonIcon: 'delete',
                },
              } as GenericWarningDialogData,
            })
            .afterClosed(),
        );
        if (result === 'delete') {
          this.deleteRide();
          return;
        } else if (result !== 'save') {
          this.saving.set(false);
          return;
        }
      }
    }

    if (this.showDoubleBookingWarning) {
      await this.getUserWarnings(true);
      if (Object.keys(this.userWarnings()).length > 0) {
        console.log('User warnings present:', this.userWarnings());
        this.snackBar.open('Er zijn waarschuwingen voor één of meer passagiers.', 'X', {
          duration: 5000,
        });
        this.saving.set(false);
        return;
      }
    }

    await this.checkIfRideIsValid(
      this.rideForm.value.start,
      this.rideForm.value.end,
      this.rideForm.get('vehicleId').value,
    );

    this.rideException = null;
    const exception = getExceptionsForDay(
      this.rideForm.value.start,
      this.rideForm.value.end,
      this.daypartExceptions(),
    );
    if (exception) {
      const result = await firstValueFrom(
        this.dialog
          .open(WarningDialog, {
            width: '500px',
            autoFocus: false,
            data: {
              dialogType: 'dayPartException',
              optionalData: {
                exceptionName: exception.title,
                exceptionDescription: exception.description,
              },
            } as GenericWarningDialogData,
          })
          .afterClosed(),
      );
      if (!result) {
        this.saving.set(false);
        return;
      }
      this.rideException = exception;
    }

    let type: string = '';
    if (
      (this.retourAlreadyBooked && this.vehicleAlreadyBooked) ||
      (this.retourAlreadyBooked && !this.vehicleAlreadyBooked)
    ) {
      type = 'retourAlreadyBooked';
    } else if (
      !this.retourAlreadyBooked &&
      this.vehicleAlreadyBooked &&
      this.showDoubleBookingWarning
    ) {
      type = 'vehicleAlreadyBooked';
    }

    if (
      (this.retourAlreadyBooked && this.showDoubleBookingWarning) ||
      (this.vehicleAlreadyBooked && this.showDoubleBookingWarning)
    ) {
      const dialogRef = this.dialog.open(WarningDialog, {
        width: '600px',
        data: { dialogType: type },
      });
      dialogRef.afterClosed().subscribe(async (result) => {
        if (result) {
          await this.saveRide();
        } else {
          this.saving.set(false);
        }
      });
    } else {
      await this.saveRide();
    }
  }

  async saveRide() {
    const selectedVehicle = this.selectedVehicle();
    if (
      !this.preserveHistoricalVehicleAssignment() &&
      selectedVehicle?.vehicleType === 'private' &&
      (!selectedVehicle.driverId ||
        this.driverId() !== selectedVehicle.driverId ||
        (dataStore.generalSettings.coDriverEnabled &&
          (this.coDriverId() ?? null) !== (selectedVehicle.coDriverId ?? null)))
    ) {
      this.saving.set(false);
      this.snackBar.open('Controleer de vaste chauffeurs van dit privévoertuig.', 'X', {
        duration: 5000,
      });
      return;
    }
    const rideRef = doc(db, `organisations/${this.organisationId}/rides/${this.rideId}`);
    const saveObj = { ...this.data.ride } as Ride;
    saveObj.rideVersion = 2; // DO NOT CHANGE THIS unless major changes have been made to the ride structure, as this is used to differentiate between old and new(er) ride structures

    const selectedProduct = this.products().find((product) => {
      return product.name === this.rideForm.value.rideProduct;
    });
    saveObj.rideProduct = selectedProduct as Product;

    const usersObj = {};
    this.updateSeats();
    if (this.data.ride.users) {
      Object.keys(this.data.ride.users).forEach((id) => {
        usersObj[id] = deleteField();
      });
    }
    const userIds = [];
    for (const user of this.users()) {
      if (typeof user.email === 'undefined') {
        user.email = null;
      }
      userIds.push(user.id);

      if (!user.rideProduct) {
        user.rideProduct = selectedProduct;
      }
      if (user.userPickupTime) {
        user.userPickupTime = valueToDate(user.userPickupTime);
      }
      if (user.userDropoffTime) {
        user.userDropoffTime = valueToDate(user.userDropoffTime);
      }
      usersObj[user.id] = user;

      const passenger = (
        await getDoc(doc(db, `organisations/${this.organisationId}/users/${user.id}`))
      ).data() as User;
      let destinations: string[] = passenger?.recentDestinations
        ? passenger.recentDestinations
        : [];

      const userFromLocation = getFullLocationString(user.from);
      const userToLocation = getFullLocationString(user.to);

      if (destinations.includes(userFromLocation)) {
        destinations.splice(destinations.indexOf(userFromLocation), 1);
      }
      destinations.unshift(userFromLocation);

      if (destinations.includes(userToLocation)) {
        destinations.splice(destinations.indexOf(userToLocation), 1);
      }
      destinations.unshift(userToLocation);

      if (destinations.length > 10) {
        destinations = destinations.slice(0, 10);
      }
      if (user.id && passenger) {
        await setDoc(
          doc(db, `organisations/${this.organisationId}/users/${user.id}`),
          {
            recentDestinations: destinations,
          },
          { merge: true },
        );
      }
    }
    saveObj.userIds = userIds;
    saveObj.special = this.special;
    if (!this.preserveHistoricalVehicleAssignment()) {
      saveObj.driverId = this.driverId();
    }
    saveObj.start = this.rideForm.value.start;
    saveObj.end = this.rideForm.value.end;
    saveObj.comments = this.rideForm.value.comments;
    saveObj.special = this.rideForm.value.special;
    if (!this.preserveHistoricalVehicleAssignment()) {
      saveObj.vehicleId = this.rideForm.getRawValue().vehicleId;
      saveObj.vehicleRef = `/organisations/${this.organisationId}/vehicles/${this.rideForm.getRawValue().vehicleId}`;
    }
    saveObj.availableSeats = this.availableSeats();
    saveObj.characteristics = this.characteristics();
    if (
      !this.preserveHistoricalVehicleAssignment() &&
      this.coDriverId() &&
      dataStore.generalSettings.coDriverEnabled
    ) {
      saveObj.coDriverId = this.coDriverId();
    } else if (
      !this.preserveHistoricalVehicleAssignment() &&
      selectedVehicle?.vehicleType === 'private'
    ) {
      saveObj.coDriverId = deleteField() as any;
    }
    if (this.data.ride.isRetour && !this.data.ride.retourParentId) {
      saveObj.retourParentId = this.retourParentId;
    }

    if (this.users().length !== 0) {
      saveObj.users = usersObj as Map<string, any>;
      let locationsOrder = [];
      this.timeSlots().forEach((timeSlot) => {
        locationsOrder = locationsOrder.concat(timeSlot.locations);
      });
      saveObj.locationsOrder = locationsOrder;
    } else {
      saveObj.users = deleteField() as any;
      saveObj.locationsOrder = deleteField() as any;
    }

    if (this.data.ride.id) {
      await this.logRideChanges(saveObj, rideRef);
    } else if (this.newRide) {
      await addDoc(collection(rideRef, 'edits'), {
        changes: [{ field: 'createdRide', text: 'Rit gecreëerd' }],
        date: new Date(),
        editedByEmail: dataStore.rootUser.email,
        editedByUid: dataStore.rootUser.id,
      });
    }

    saveObj.comments = saveObj.comments !== null ? saveObj.comments : '';
    Object.keys(saveObj).forEach((key) => {
      if (saveObj[key] == null) {
        saveObj[key] = deleteField() as any;
      }
    });

    if (saveObj.start) {
      const startDate = moment(valueToDate(saveObj.start));
      saveObj.year = startDate.year();
      saveObj.month = startDate.month();
      saveObj.week = getWeekString(startDate.toDate());
      saveObj.day = startDate.date();
    }

    saveObj.title = getRideTitle(saveObj);

    if (this.rideException) {
      saveObj.exception = {
        id: this.rideException.id,
        title: this.rideException.title,
        description: this.rideException.description,
      };
    }

    if (this.data.ride.parentRideId) {
      saveObj.parentRideId = this.data.ride.id;
      const oldRide = structuredClone(this.data.ride);
      if (this.data.oldRideDates?.start && this.data.oldRideDates?.end) {
        oldRide.start = this.data.oldRideDates.start;
        oldRide.end = this.data.oldRideDates.end;
      }
      const dialogRef = this.dialog.open(CopyUpdateComponent, {
        width: '600px',
        data: {
          oldRide: oldRide,
          newRide: structuredClone(saveObj),
        },
      });
      dialogRef.afterClosed().subscribe(async (result) => {
        if (result === undefined) {
          this.saving.set(false);
        } else if (result === 'done') {
          await setDoc(rideRef, saveObj, {
            merge: true,
          });
          this.notifyParticipantsRideEdited();
        } else if (result === false) {
          saveObj.parentRideId = null;
          saveObj.hasCopy = null;
          await setDoc(rideRef, saveObj, {
            merge: true,
          });
          this.notifyParticipantsRideEdited();
        }
      });
    } else {
      await setDoc(rideRef, saveObj, {
        merge: true,
      });
      this.notifyParticipantsRideEdited();
    }
    console.log('Saved ride: ', saveObj);
  }

  private describeLocationSource(location: unknown) {
    if (typeof location === 'string') {
      return { source: 'string', value: location };
    }

    if (!location || typeof location !== 'object') {
      return { source: typeof location, value: location ?? null };
    }

    const typedLocation = location as {
      id?: string;
      name?: string;
      address?: string;
      coordinates?: { latitude: number; longitude: number };
      coordinatesFor?: string;
    };

    return {
      source: 'object',
      id: typedLocation.id ?? null,
      name: typedLocation.name ?? null,
      address: typedLocation.address ?? null,
      hasCoordinates: !!typedLocation.coordinates,
      coordinatesFor: typedLocation.coordinatesFor ?? null,
    };
  }

  async logRideChanges(saveObj: Ride, rideRef) {
    const changes = [];
    let originalStart = this.data.ride['start'];
    let originalEnd = this.data.ride['end'];
    if (this.data.oldRideDates?.start && this.data.oldRideDates?.end) {
      originalStart = this.data.oldRideDates.start;
      originalEnd = this.data.oldRideDates.end;
    }

    if (this.data.ride['comments'] !== saveObj['comments']) {
      changes.push({
        field: 'comments',
        from: this.data.ride['comments'],
        to: saveObj['comments'],
      });
    }
    if (!moment(originalStart).isSame(saveObj['start'])) {
      changes.push({
        field: 'start',
        from: originalStart,
        to: saveObj['start'],
      });
    }
    if (!moment(originalEnd).isSame(saveObj['end'])) {
      changes.push({
        field: 'end',
        from: originalEnd,
        to: saveObj['end'],
      });
    }
    if (this.data.ride.rideProduct?.id !== saveObj.rideProduct?.id) {
      changes.push({
        field: 'rideProduct',
        from: this.data.ride['rideProduct'],
        to: saveObj['rideProduct'],
      });
    }
    if (this.data.ride['special'] !== saveObj['special']) {
      if (saveObj['special'] === true) {
        changes.push({
          field: 'special',
          from: 'Normale rit',
          to: 'Bijzondere rit',
        });
      } else if (saveObj['special'] === false) {
        changes.push({
          field: 'special',
          from: 'Bijzondere rit',
          to: 'Normale rit',
        });
      }
    }
    if (this.data.ride['vehicleId'] !== saveObj['vehicleId']) {
      const oldVehicle = this.vehicles().find((vehicle) => vehicle.id === this.data.ride.vehicleId);
      const newVehicle = this.vehicles().find((vehicle) => vehicle.id === saveObj['vehicleId']);
      changes.push({
        field: 'vehicleId',
        from: oldVehicle?.['name'] ?? this.data.ride['vehicleId'] ?? null,
        to: newVehicle?.['name'] ?? saveObj['vehicleId'] ?? null,
      });
    }
    const temporaryUserArray: User[] = [];
    for (const user of this.addedUsers()) {
      temporaryUserArray.push(user);
      const index = this.removedUsers().findIndex((x) => x.id === (user.id as string));
      if (index !== -1) {
        this.addedUsers.update((users) => {
          const newUsers = [...users];
          newUsers.splice(index, 1);
          return newUsers;
        });
      }
    }
    for (const user of this.removedUsers()) {
      const index = temporaryUserArray.findIndex((x) => x.id === (user.id as string));
      if (index !== -1) {
        this.removedUsers.update((users) => {
          const newUsers = [...users];
          newUsers.splice(index, 1);
          return newUsers;
        });
      }
    }
    if (this.addedUsers().length > 0) {
      changes.push({
        field: 'addedUsers',
        users: this.addedUsers(),
      });
    }
    if (this.removedUsers().length > 0) {
      changes.push({
        field: 'removedUsers',
        users: this.removedUsers(),
      });
    }
    const changedUsers = {};
    for (const user of Object.values(saveObj.users ?? {}) as UserInRide[]) {
      if (
        this.data.ride?.users &&
        this.data.ride?.users[user.id] &&
        user !== this.data.ride?.users[user.id]
      ) {
        changedUsers[user.id] = getObjectDifferences(this.data.ride?.users[user.id], user);
      }
    }
    if (Object.keys(changedUsers).length > 0) {
      changes.push({
        field: 'changedUsers',
        users: changedUsers,
      });
    }

    if (changes.length > 0) {
      await addDoc(collection(rideRef, 'edits'), {
        changes: changes,
        date: new Date(),
        editedByEmail: dataStore.rootUser.email,
        editedByUid: dataStore.rootUser.id,
      });
    }
  }

  async checkIfRideIsValid(startDate, endDate, vehicleId) {
    this.vehicleAlreadyBooked = false;
    this.retourAlreadyBooked = false;
    const day = startDate.getDate();
    const month = startDate.getMonth();
    const year = startDate.getFullYear();
    const querySnapshot = await getDocs(
      query(
        collection(db, `organisations/${this.organisationId}/rides`),
        where('day', '==', day),
        where('month', '==', month),
        where('year', '==', year),
      ),
    );
    querySnapshot.forEach((rideDoc) => {
      const ride = { ...rideDoc.data(), id: rideDoc.id } as Ride;
      const newRideStart = moment(startDate);
      const existingRideStart = moment(valueToDate(ride.start));
      const newRideEnd = moment(endDate);
      const existingRideEnd = moment(valueToDate(ride.end));
      if (this.retourParentId && ride.retourParentId === this.retourParentId) {
        this.retourAlreadyBooked = true;
      }
      if (
        newRideStart.isBetween(existingRideStart, existingRideEnd) ||
        newRideEnd.isBetween(existingRideStart, existingRideEnd) ||
        existingRideStart.isBetween(newRideStart, newRideEnd) ||
        existingRideEnd.isBetween(newRideStart, newRideEnd) ||
        (newRideStart.isSame(existingRideStart) && newRideEnd.isSame(existingRideEnd))
      ) {
        if (ride.vehicleId === vehicleId) {
          if (ride.id !== this.rideId) {
            this.vehicleAlreadyBooked = true;
          }
        }
      }
    });
  }

  notifyParticipantsRideEdited() {
    const deletedUsers: UserInRide[] = [];
    const addedUsers: UserInRide[] = [];
    const retainedUsers: UserInRide[] = [];

    this.originalUserList().forEach((user) => {
      if (!this.users().find(({ title }) => title === user.title)) {
        deletedUsers.push(user);
      }
    });
    this.users().forEach((user) => {
      if (!this.originalUserList().find(({ title }) => title === user.title)) {
        addedUsers.push(user);
      } else {
        retainedUsers.push(user);
      }
    });
    if (
      (this.organisationNotificationSettings.email.enabled ||
        this.organisationNotificationSettings.sms.enabled) &&
      (this.users().length > 0 || this.originalUserList().length > 0)
    ) {
      const dialogRef = this.dialog.open(NotifyUsersRideDialog, {
        width: '600px',
        disableClose: true,
      });
      dialogRef.afterClosed().subscribe(async (result) => {
        if (result) {
          if (
            this.organisationNotificationSettings.email.RideConfirmed ||
            this.organisationNotificationSettings.sms.RideConfirmed
          ) {
            if (addedUsers.length > 0) {
              for (const user of addedUsers) {
                planNotificationParticipant(user, this.organisationId);
              }
            }
          }

          if (deletedUsers.length > 0) {
            if (
              this.organisationNotificationSettings.email.RideDeleted ||
              this.organisationNotificationSettings.sms.RideDeleted
            ) {
              for (const user of deletedUsers) {
                deleteRideNotificationParticipant(user, this.organisationId);
              }
            }
          }

          if (retainedUsers.length > 0) {
            if (
              this.organisationNotificationSettings.email.RideChanged ||
              this.organisationNotificationSettings.sms.RideChanged
            ) {
              for (const user of retainedUsers) {
                const oldUserData = this.originalUserList().find(
                  ({ title }) => title === user.title,
                );
                if (!oldUserData) {
                  continue;
                }
                const startChanged = !moment(valueToDate(oldUserData.userPickupTime)).isSame(
                  moment(valueToDate(user.userPickupTime)),
                );
                const fromChanged = oldUserData.from !== user.from;
                const toChanged = oldUserData.to !== user.to;
                if (startChanged || fromChanged || toChanged) {
                  changedRideNotificationParticipant(this.organisationId, user);
                }
              }
            }
          }
        }
        this.handleReminders(addedUsers, deletedUsers, retainedUsers);
        this.dialogRef.close(true);
      });
    } else {
      this.handleReminders(addedUsers, deletedUsers, retainedUsers);
      this.dialogRef.close(true);
    }
  }

  handleReminders(
    addedUsers: UserInRide[],
    deletedUsers: UserInRide[],
    retainedUsers: UserInRide[],
  ) {
    for (const user of addedUsers) {
      addToReminderNotificationQueue(this.rideId, user, this.organisationId);
    }
    if (deletedUsers.length > 0) {
      for (const user of deletedUsers) {
        removeFromReminderQueue(this.rideId, user, this.organisationId);
      }
    }
    if (retainedUsers.length > 0) {
      for (const user of retainedUsers) {
        const oldUserData = this.originalUserList().find(({ title }) => title === user.title);
        if (!oldUserData) {
          continue;
        }
        const startChanged = !moment(valueToDate(oldUserData.userPickupTime)).isSame(
          moment(valueToDate(user.userPickupTime)),
        );
        const fromChanged = oldUserData.from !== user.from;
        const toChanged = oldUserData.to !== user.to;
        if (startChanged || fromChanged || toChanged) {
          changeReminderInQueue(this.rideId, user, this.organisationId);
        }
      }
    }
  }

  notifyParticipantsRideDeleted() {
    if (this.users().length === 0) {
      this.saving.set(false);
      this.dialogRef.close();
      return;
    }

    if (
      (this.organisationNotificationSettings.email.enabled &&
        this.organisationNotificationSettings.email.RideDeleted) ||
      (this.organisationNotificationSettings.sms.enabled &&
        this.organisationNotificationSettings.sms.RideDeleted)
    ) {
      const dialogRef = this.dialog.open(NotifyUsersRideDialog, {
        width: '600px',
        disableClose: true,
        data: { deleted: true },
      });
      dialogRef.afterClosed().subscribe(async (result) => {
        if (result) {
          for (const user of this.users()) {
            deleteRideNotificationParticipant(user, this.organisationId);
          }
        }
        for (const user of this.users()) {
          removeFromReminderQueue(this.rideId, user, this.organisationId);
        }
        this.saving.set(false);
        this.dialogRef.close();
      });
    } else {
      for (const user of this.users()) {
        removeFromReminderQueue(this.rideId, user, this.organisationId);
      }
      this.saving.set(false);
      this.dialogRef.close();
    }
  }

  seatsValidator() {
    return (): ValidationErrors | null => {
      if (!this.selectedVehicle()) {
        return null;
      }
      if (this.availableSeats() < 0) {
        return { seatsValidator: true };
      }
      return null;
    };
  }

  privateVehicleEligibilityValidator() {
    return (): ValidationErrors | null => {
      return this.getPrivateVehicleEligibilityError() ? { privateVehicleEligibility: true } : null;
    };
  }

  rideProductValidator(): ValidatorFn {
    return (c: AbstractControl): ValidationErrors | null => {
      if (
        c.value &&
        !this.products().some((product) => product.name.toLowerCase() === c.value.toLowerCase())
      ) {
        return { invalidProduct: true };
      }
      return null;
    };
  }

  hasUserWarnings(userId: string): boolean {
    if (!this.userWarnings()[userId]) return false;
    return Object.keys(this.userWarnings()[userId]).some((key) => key !== 'from' && key !== 'to');
  }

  getRideData() {
    const ride = { ...this.rideForm.value, users: this.users() } as Ride;
    return { ...ride, locationsOrder: buildLocationsOrder(ride) } as Ride;
  }

  rideHasChanges(): boolean {
    const originalUsers = Object.values(this.data.ride.users ?? {});
    return (
      this.rideForm.dirty ||
      JSON.stringify(this.characteristics()) !==
        JSON.stringify(this.data.ride?.characteristics ?? []) ||
      JSON.stringify(this.users()) !== JSON.stringify(originalUsers)
    );
  }
}
